import React, {
  ChangeEvent,
  ReactNode,
  useMemo,
  useEffect,
  useState,
  useRef,
  forwardRef,
} from 'react';
import clsx from 'clsx';
import { FormField } from 'components/helpers/Atoms/FormElements/FormField';
import { PasswordInput } from 'components/helpers/Atoms/FormElements/PasswordInput';
import { PasswordValidationRules } from 'components/helpers/Atoms/FormElements/PasswordValidationRules';
import {
  PasswordValidationRule,
  getPasswordRuleStatus,
  passwordRules,
} from 'lib/utils/validation/password-validation';
import useDictionary from 'lib/hooks/useDictionary';
import styles from './NewPasswordWithCriteria.module.scss';
import config from 'sitecore.config';

export interface NewPasswordWithCriteriaProps {
  // FormField props
  label?: ReactNode;
  labelFor?: string;
  required?: boolean;
  className?: string;

  // PasswordInput props
  id: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  showPasswordToggle?: boolean; // Default: true

  // Validation props
  validationRules?: PasswordValidationRule[]; // Rules to display (defaults to defaultPasswordRules)
  showLeftIcon?: boolean; // Show dynamic left icon (green checkmark/red X) - default: true

  // Validation timing
  validateOnBlur?: boolean; // Validate when field loses focus (default: false)
  validateOnChange?: boolean; // Validate as user types (default: true)

  // External error override
  error?: string; // External error message that overrides internal validation error
  showInlineError?: boolean; // Override to show inline error (default: follows validation timing)

  // Callbacks
  onValidationChange?: (isValid: boolean) => void; // Callback when validation state changes
}

const NewPasswordWithCriteria = forwardRef<HTMLInputElement, NewPasswordWithCriteriaProps>(
  (
    {
      label = 'New password',
      labelFor,
      required = false,
      className,
      id,
      value,
      onChange,
      placeholder,
      disabled,
      showPasswordToggle = true,
      validationRules,
      showLeftIcon = true,
      validateOnBlur = false,
      validateOnChange = true,
      error: externalError,
      showInlineError: externalShowInlineError,
      onValidationChange,
    },
    ref
  ) => {
    const [hasBlurred, setHasBlurred] = useState(false);
    const { getDictionaryValue } = useDictionary();

    // Use provided rules or default brand rules
    const effectiveRules = validationRules ?? passwordRules;

    // Calculate rule status
    const ruleStatus = useMemo(
      () => getPasswordRuleStatus(value, effectiveRules),
      [value, effectiveRules]
    );

    // Calculate overall validity
    const isValid = useMemo(() => {
      return Object.values(ruleStatus).every((status) => status === true);
    }, [ruleStatus]);

    // Generate dynamic error message based on failing criteria
    // Resolve dictionary keys when rendering error messages
    const calculatedDynamicError = useMemo(() => {
      if (value.length === 0) return null; // Don't show error when empty

      const failedRules = effectiveRules.filter((rule) => !ruleStatus[rule.id]);
      if (failedRules.length === 0) return null; // All rules passed

      const siteName = config.defaultSite;

      // NAF (GMFS) requires a specific inline error message.
      if (siteName === 'GMFS') {
        return getDictionaryValue(
          'Components.MyAccount.ErrorMessages.PasswordInlineError',
          'Password must be at least 8 characters and contain at least 3 of the following types: uppercase letter, lowercase letter, number and symbol.'
        );
      }

      // Resolve dictionary keys for labels
      const ruleLabels = failedRules.map((rule) =>
        rule.dictionaryKey ? getDictionaryValue(rule.dictionaryKey, rule.label) : rule.label
      );

      // Generate error message from failed rules
      const passwordMust = getDictionaryValue('Components.MyAccount.PasswordMust', 'Password must');

      if (ruleLabels.length === 1) {
        return `${passwordMust} ${ruleLabels[0]}.`;
      } else if (ruleLabels.length === 2) {
        return `${passwordMust} ${ruleLabels[0]} and ${ruleLabels[1]}.`;
      } else {
        // For 3+ rules, list them with commas and "and" before the last one
        const lastLabel = ruleLabels.pop();
        return `${passwordMust} ${ruleLabels.join(', ')}, and ${lastLabel}.`;
      }
    }, [value, effectiveRules, ruleStatus, getDictionaryValue]);

    // Determine if we should show the error based on validation timing
    const shouldShowError = useMemo(() => {
      if (!calculatedDynamicError) return false;
      // Show error if:
      // 1. validateOnChange is true (validate as user types), OR
      // 2. validateOnBlur is true and field has been blurred
      return validateOnChange || (validateOnBlur && hasBlurred);
    }, [calculatedDynamicError, validateOnChange, validateOnBlur, hasBlurred]);

    // Use external error if provided, otherwise use internal dynamic error
    const dynamicError = shouldShowError ? calculatedDynamicError : null;
    const finalError = externalError || dynamicError;
    const hasError = !!finalError;

    // Determine if inline error should be shown
    const shouldShowInlineError =
      externalShowInlineError !== undefined ? externalShowInlineError : hasError;

    // Handle blur event
    const handleBlur = () => {
      setHasBlurred(true);
      setIsFocused(false);
    };

    // Handle change event
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e);
    };

    // Determine which icon to show (always shows real-time validation state)
    const leftIcon = useMemo(() => {
      if (!showLeftIcon) return undefined;
      if (value.length === 0) return undefined; // No icon when empty
      return isValid ? 'bullet-check-fill' : 'close-circle';
    }, [showLeftIcon, value.length, isValid]);

    // Determine if input should show error outline
    const showErrorOutline = useMemo(() => {
      if (value.length === 0 && !externalError) return false; // No outline when empty (unless external error)
      // Show outline if error should be shown
      return hasError;
    }, [hasError, value.length, externalError]);

    // Track previous validation state to avoid unnecessary callbacks
    const prevValidationRef = useRef<{ isValid: boolean; valueLength: number } | null>(null);
    const onValidationChangeRef = useRef(onValidationChange);

    // Keep the ref updated with the latest callback
    useEffect(() => {
      onValidationChangeRef.current = onValidationChange;
    }, [onValidationChange]);

    // Notify parent of validation change (only when values actually change)
    useEffect(() => {
      const prevState = prevValidationRef.current;
      const currentState = { isValid, valueLength: value.length };

      // Only call callback if validation state actually changed and value is not empty
      if (
        (prevState === null ||
          prevState.isValid !== currentState.isValid ||
          prevState.valueLength !== currentState.valueLength) &&
        value.length > 0
      ) {
        prevValidationRef.current = currentState;
        if (onValidationChangeRef.current) {
          onValidationChangeRef.current(isValid);
        }
      }
    }, [isValid, value.length]);

    // Generate unique error ID for aria-describedby
    const errorId = hasError && shouldShowInlineError ? `${id}-error` : undefined;
    const [isFocused, setIsFocused] = useState(false);
    const rulesId = `${id}-rules`;
    // When an error is visible, only point to the error — including rulesId would
    // cause a second announcement when isFocused changes the attribute mid-focus.
    // When there is no error, point to the criteria list while focused.
    const describedBy =
      [errorId, !hasError && isFocused ? rulesId : undefined].filter(Boolean).join(' ') ||
      undefined;
    useEffect(() => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      if (!el) return;
      if (describedBy) el.setAttribute('aria-describedby', describedBy);
      else el.removeAttribute('aria-describedby');
    }, [id, describedBy]);
    return (
      <div className={clsx(styles.newPasswordWithCriteria, className)}>
        <FormField
          label={label}
          labelFor={labelFor ?? id}
          required={required}
          error={finalError}
          showInlineError={shouldShowInlineError}
          errorId={errorId}
        >
          <PasswordInput
            ref={ref}
            id={id}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={() => setIsFocused(true)}
            placeholder={placeholder}
            disabled={disabled}
            showPasswordToggle={showPasswordToggle}
            leftIcon={leftIcon}
            leftIconValid={leftIcon ? isValid : undefined}
            error={showErrorOutline}
            aria-required={required || undefined}
          />
        </FormField>
        <PasswordValidationRules
          id={rulesId}
          rules={effectiveRules}
          ruleStatus={ruleStatus}
          isEmpty={value.length === 0}
        />
      </div>
    );
  }
);

NewPasswordWithCriteria.displayName = 'NewPasswordWithCriteria';

export default NewPasswordWithCriteria;
